import React, { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { gql, useQuery } from '@apollo/client';
import moment from 'moment';
import { useDispatch, useI18n, useSelector } from '~Hooks';
import SafTGenerator from '@baemingo/saf-t-generator';
import ReactCalendar from 'react-calendar';
import { useMutation } from '@apollo/client';
import { addToast } from '~actions';
import Button from '~Components/Button';
import Heading from '~ComponentsImproved/Typography/Heading';
import { handyBreakpoint } from '~Styles/breakpoints';
import { DarkCalendar } from '~Styles/calendar';
import { useStorage } from '~Components/Storage';
import {
  SendSaftXml,
  SendSaftXmlVariables,
  GetReportEvents,
  GetReportEventsVariables,
  GetCompanyStaff,
  GetCompanyStaffVariables,
  GetItems,
  GetItemsVariables,
  GetUser,
} from './__queries__';

export const predefinedNorwegianSafTIdMap: { [key: string]: string } = {
  Food: '4006',
  Beer: '4007',
  Wine: '4008',
  Spirits: '4009',
  Alcopop: '4010',
  'Mineral Water': '4011',
  'Tea/Coffee': '4012',
  'Other Goods': '4014',
  Other: '4999',
};

const EVENTS_QUERY = gql`
  query GetReportEvents($venueId: String!, $sentAfter: DateTime!, $lastEventDate: DateTime) {
    reportEvents(venueId: $venueId, sentAfter: $sentAfter, lastEventDate: $lastEventDate) {
      lastKey
      items
    }
  }
`;

const COMPANY_STAFF = gql`
  query GetCompanyStaff($companyId: ID!) {
    company: Company(id: $companyId) {
      id
      staffs {
        id
        name
        createdAt
        accessLevel {
          name
        }
      }
    }
  }
`;

const ITEMS_QUERY = gql`
  query GetItems($venueId: ID!) {
    venue: Venue(id: $venueId) {
      id
      name
      organizationId
      zip
      createdAt
      company {
        id
        name
        country
        defaultLanguage
        venues {
          organizationId
          location {
            name
          }
        }
        items {
          id
          title
          defaultPrice
          inventoryEOD
          vatRate
          createdAt
          categories {
            nameLang {
              en
              es
              se
              no
            }
          }
        }
        categories {
          id
          parentId
          nameLang {
            id
            en
            es
            se
            no
          }
        }
      }
    }
  }
`;

const USER_QUERY = gql`
  query GetUser {
    user {
      email
    }
  }
`;

const SEND_SAFT_XML_MUTATION = gql`
  mutation SendSaftXml(
    $data: String!
    $venueId: ID!
    $venueName: ID!
    $recipientEmail: String!
    $startDate: String!
    $endDate: String!
  ) {
    sendSaftXml(
      data: $data
      venueId: $venueId
      venueName: $venueName
      recipientEmail: $recipientEmail
      startDate: $startDate
      endDate: $endDate
    ) {
      success
    }
  }
`;

// fetch employees
const Wrapper = styled.div`
  display: flex;
  flex-direction: row;

  ${handyBreakpoint} {
    flex-direction: column;
    padding: 16px;
  }
`;

const SafTOptionsContainer = styled.div`
  display: flex;
  flex-direction: column;

  ${handyBreakpoint} {
    margin-top: 16px;
  }
`;

const TextArea = styled.textarea`
  width: 600px;
  margin-left: 20px;
  resize: none;

  ${handyBreakpoint} {
    margin-left: 0px;
    margin-top: 20px;
    padding: 10px 0px;
  }
`;

const ButtonContainer = styled.div`
  margin-top: 20px;
`;

interface Props {
  isHandy?: boolean;
}

export default function SafT({ isHandy }: Props) {
  const currentDate = new Date();
  const companyId = useSelector((state) => state.company);
  const language = useSelector((state) => state.language);
  const [, , venueId] = location.hash.split('/');

  const [storage] = useStorage();

  const dispatch = useDispatch();
  const { i18n } = useI18n();

  const [chosenDate, setChosenDate] = useState<Date | Date[]>(currentDate);
  const [sentAfter, setSentAfter] = useState(
    moment(currentDate)
      .startOf('day')
      .add(2, 'hours')
      .toISOString(),
  );
  const [endOfDay, setEndOfDay] = useState(
    moment(currentDate)
      .endOf('day')
      .add(2, 'hours')
      .toISOString(),
  );
  const [saftReport, setSaftReport] = useState('');
  const [disableSendButton, setDisableSendButton] = useState<boolean>(false);

  const [sendSaftXML] = useMutation<SendSaftXml, SendSaftXmlVariables>(SEND_SAFT_XML_MUTATION);
  const safTRef = useRef<SafTGenerator>();

  const eventsQuery = useQuery<GetReportEvents, GetReportEventsVariables>(EVENTS_QUERY, {
    variables: {
      venueId,
      sentAfter: sentAfter,
      lastEventDate: endOfDay,
    },
  });

  const staffQuery = useQuery<GetCompanyStaff, GetCompanyStaffVariables>(COMPANY_STAFF, {
    variables: {
      companyId,
    },
  });

  const itemsQuery = useQuery<GetItems, GetItemsVariables>(ITEMS_QUERY, {
    variables: {
      venueId,
    },
  });

  const userQuery = useQuery<GetUser>(USER_QUERY);

  const { items } = eventsQuery?.data?.reportEvents || {};
  const { data: staffData } = staffQuery;
  const { data: productMetadata } = itemsQuery;

  const updateSafTData = () => {
    if (!safTRef.current) {
      return;
    }
    safTRef.current.addEvents(items || []);
    safTRef.current.updateSelectedDateRange(sentAfter, endOfDay);
    safTRef.current.addStaffMetadata(staffData?.company?.staffs || []);
    safTRef.current.addProductMetadata(
      productMetadata?.venue?.company?.items?.map(
        (item: { createdAt: any; id: any; defaultPrice: any; title: any; vatRate: any }) => {
          return {
            id: item.id,
            price: item.defaultPrice,
            title: item.title,
            vatRate: item.vatRate,
            createdAt: item.createdAt,
          };
        },
      ) || [],
    );
    safTRef.current.addCategoryMetadata(
      productMetadata?.venue?.company?.categories?.map((category: any) => {
        return {
          id: category.id,
          name: category.nameLang[language],
          predefinedId: predefinedNorwegianSafTIdMap[category.nameLang[language]],
        };
      }) || [],
    );
  };

  useEffect(() => {
    // Refetch events if time range changes (no need to refetch other data)
    eventsQuery.refetch();
  }, [sentAfter, endOfDay]);

  useEffect(() => {
    if (itemsQuery.loading || staffQuery.loading || eventsQuery.loading) return;
    if (!safTRef.current) {
      // Initialise SAF-T generator if it hasn't been initialised yet
      safTRef.current = new SafTGenerator({
        companyName: productMetadata?.venue.company.venues[0].location.name || '',
        organizationNr: productMetadata?.venue.company.venues[0].organizationId || '',
        startDate: sentAfter,
        endDate: endOfDay,
        currentDate: currentDate.toISOString(),
        cashRegister: {
          name: storage.taxComplianceDeviceName || '-',
          id: storage.taxComplianceDeviceId || '-',
        },
      });
    }

    // Update all data inside the SAF-T generator
    updateSafTData();

    // Generate a new SAF-T report
    const generatedSaftReport = safTRef.current.generate();
    setSaftReport(generatedSaftReport);
  }, [itemsQuery, staffQuery, eventsQuery]);

  const sendReportEmail = async () => {
    if (disableSendButton) {
      return;
    }
    try {
      sendSaftXML({
        variables: {
          data: saftReport,
          venueId,
          venueName: productMetadata?.venue.company.venues[0].location.name || '',
          recipientEmail: userQuery?.data?.user?.email || '',
          startDate: moment(sentAfter).format('YYYY-MM-DD'),
          endDate: moment(endOfDay).format('YYYY-MM-DD'),
        },
      });
      setDisableSendButton(() => true);
      dispatch(addToast(`${i18n('TOAST.Email.Sent')} ${userQuery?.data?.user?.email}`, true));

      await setTimeout(() => {
        setDisableSendButton(() => false);
      }, 3000);
    } catch (e) {
      dispatch(addToast(i18n('TOAST.Email.Not.Sent'), true));
    }
  };

  const Content = () => {
    const content = (
      <>
        <ReactCalendar
          onChange={(date) => {
            if (!Array.isArray(date)) return;
            setChosenDate(date);
            setSentAfter(
              moment(date[0])
                .startOf('day')
                .add(2, 'hours')
                .toISOString(),
            );
            setEndOfDay(
              moment(date[1])
                .endOf('day')
                .add(2, 'hours')
                .toISOString(),
            );
          }}
          maxDate={currentDate}
          selectRange={true}
          value={chosenDate}
        />
        <ButtonContainer>
          <Button
            background={isHandy ? 'var(--colorBlueGray1100)' : undefined}
            handleClick={() => sendReportEmail()}
            disabled={disableSendButton}
            text={i18n('SafT.Action.EmailSafTFile')}
          />
        </ButtonContainer>
      </>
    );

    return isHandy ? <DarkCalendar>{content}</DarkCalendar> : <>{content}</>;
  };

  return (
    <Wrapper>
      {isHandy && (
        <Heading level={2} color="#fff">
          SAF-T
        </Heading>
      )}
      <SafTOptionsContainer>
        <Content />
      </SafTOptionsContainer>
      {!isHandy && <TextArea value={saftReport} readOnly />}
    </Wrapper>
  );
}
